import { gql } from "@apollo/client/core";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";

import type { User } from "@/features/auth";
import type { PageInputDto, PaginatedResult } from "@/shared/types/pagination";

export interface Organization {
  createdAt: string
  deletedAt: string | null
  id: string
  name: string
  shortName: string
  updatedAt: string
}

// ── Sort ──────────────────────────────────────────────────────────────────────

export type OrganizationSortDirection = "ASC" | "DESC";

export interface OrganizationSortInput {
  field: string
  direction: OrganizationSortDirection
}

// ── Query — list ──────────────────────────────────────────────────────────────

export type OrganizationStatusFilter = "ACTIVE" | "ARCHIVED" | "ALL";

export interface OrganizationsQueryVariables {
  pagination: PageInputDto
  sort?: OrganizationSortInput[] | null
  search?: string | null
  filter?: OrganizationStatusFilter | null
  [key: string]: unknown
}

export interface OrganizationsQueryResult {
  organizations: PaginatedResult<Organization>
}

// ── Create ────────────────────────────────────────────────────────────────────

export interface CreateOrganizationInput {
  name: string
  shortName: string
}

export interface CreateOrganizationVariables {
  input: CreateOrganizationInput
}

export interface CreateOrganizationResult {
  createOrganization: Organization
}

// ── Update ────────────────────────────────────────────────────────────────────

export interface UpdateOrganizationInput {
  id: string
  name: string
}

export interface UpdateOrganizationVariables {
  input: UpdateOrganizationInput
}

export interface UpdateOrganizationResult {
  updateOrganization: Organization
}

// ── Delete ────────────────────────────────────────────────────────────────────

export interface DeleteOrganizationVariables {
  deleteOrganizationId: string
}

export interface DeleteOrganizationResult {
  deleteOrganization: boolean
}

// ── Single org ────────────────────────────────────────────────────────────────

export interface GetOrganizationVariables {
  organizationId: string

  [key: string]: unknown
}

export interface GetOrganizationResult {
  organization: Organization
}

// ── Query — generate short name suggestions ──────────────────────────────────

export interface GenerateOrganizationShortNamesVariables {
  organizationName: string
  [key: string]: unknown
}

export interface GenerateOrganizationShortNamesResult {
  generateOrganizationShortNames: string[]
}

// ── Query — check short name availability ─────────────────────────────────────

export interface CheckOrganizationShortNameVariables {
  shortName: string
  [key: string]: unknown
}

export interface CheckOrganizationShortNameResult {
  checkOrganizationShortName: boolean
}

// ── Documents ─────────────────────────────────────────────────────────────────

export const GET_ORGANIZATIONS: TypedDocumentNode<
  OrganizationsQueryResult,
  OrganizationsQueryVariables
> = gql`
    query Organizations(
        $pagination: PageInputDto!
        $sort: [OrganizationSortInput!]
        $search: String
        $filter: OrganizationStatusFilter
    ) {
        organizations(pagination: $pagination, sort: $sort, search: $search, filter: $filter) {
            pagination {
                page
                pageSize
                total
            }
            data {
                createdAt
                deletedAt
                id
                name
                shortName
                updatedAt
            }
        }
    }
`;

export const CREATE_ORGANIZATION: TypedDocumentNode<
  CreateOrganizationResult,
  CreateOrganizationVariables
> = gql`
    mutation CreateOrganization($input: CreateOrganizationInput!) {
        createOrganization(input: $input) {
            createdAt
            id
            name
            shortName
            updatedAt
        }
    }
`;

export const UPDATE_ORGANIZATION: TypedDocumentNode<
  UpdateOrganizationResult,
  UpdateOrganizationVariables
> = gql`
    mutation UpdateOrganization($input: UpdateOrganizationInput!) {
        updateOrganization(input: $input) {
            createdAt
            deletedAt
            id
            name
            shortName
            updatedAt
        }
    }
`;

export const DELETE_ORGANIZATION: TypedDocumentNode<
  DeleteOrganizationResult,
  DeleteOrganizationVariables
> = gql`
    mutation DeleteOrganization($deleteOrganizationId: String!) {
        deleteOrganization(id: $deleteOrganizationId)
    }
`;

// ── Restore ───────────────────────────────────────────────────────────────────

export interface RestoreOrganizationVariables {
  restoreOrganizationId: string
}

export interface RestoreOrganizationResult {
  restoreOrganization: boolean
}

export const RESTORE_ORGANIZATION: TypedDocumentNode<
  RestoreOrganizationResult,
  RestoreOrganizationVariables
> = gql`
    mutation RestoreOrganization($restoreOrganizationId: String!) {
        restoreOrganization(id: $restoreOrganizationId)
    }
`;

export const GET_ORGANIZATION: TypedDocumentNode<
  GetOrganizationResult,
  GetOrganizationVariables
> = gql`
    query Organization($organizationId: String!) {
        organization(id: $organizationId) {
            createdAt
            deletedAt
            id
            name
            shortName
            updatedAt
        }
    }
`;

export const GENERATE_ORGANIZATION_SHORT_NAMES: TypedDocumentNode<
  GenerateOrganizationShortNamesResult,
  GenerateOrganizationShortNamesVariables
> = gql`
    query GenerateOrganizationShortNames($organizationName: String!) {
        generateOrganizationShortNames(organizationName: $organizationName)
    }
`;

export const CHECK_ORGANIZATION_SHORT_NAME: TypedDocumentNode<
  CheckOrganizationShortNameResult,
  CheckOrganizationShortNameVariables
> = gql`
    query CheckOrganizationShortName($shortName: String!) {
        checkOrganizationShortName(shortName: $shortName)
    }
`;

// ── Switch org ────────────────────────────────────────────────────────────────

export interface SwitchOrgMutationVariables {
  orgId: string
}

export interface SwitchOrgMutationResult {
  switchOrg: {
    accessToken: string
    user: User
  }
}

export const SWITCH_ORG_MUTATION: TypedDocumentNode<
  SwitchOrgMutationResult,
  SwitchOrgMutationVariables
> = gql`
    mutation SwitchOrg($orgId: String!) {
        switchOrg(orgId: $orgId) {
            accessToken
            user {
                id
                email
                name
                role
                orgId
                orgPermissions {
                    orgId
                    orgName
                    permissions
                }
                createdAt
                updatedAt
            }
        }
    }
`;

// ── Organization members (preview, max 5) ─────────────────────────────────

export interface OrganizationMember {
  id: string
  name: string
  email: string
  createdAt: string
}

export interface OrganizationMembersVariables {
  pagination: PageInputDto
}

export interface OrganizationMembersResult {
  users: PaginatedResult<OrganizationMember>
}

export const GET_ORGANIZATION_MEMBERS: TypedDocumentNode<
  OrganizationMembersResult,
  OrganizationMembersVariables
> = gql`
    query OrganizationMembers($pagination: PageInputDto!) {
        users(pagination: $pagination) {
            pagination {
                page
                pageSize
                total
            }
            data {
                id
                name
                email
                createdAt
            }
        }
    }
`;
